import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT } from "@/components/ContactCards";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/sale-and-purchase", label: "Sale & Purchase", testId: "nav-sp-link" },
  { to: "/demolition-recycling", label: "Demolition & Recycling", testId: "nav-demolition-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-[#1E3A6E]/50 bg-[#030F26]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="Dice International home">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-[#C5A059]" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-5 lg:flex">
          <a
            data-testid="nav-phone-link"
            href={`tel:${CONTACT.phoneRaw}`}
            className="flex items-center gap-2 font-mono text-xs tracking-wider text-slate-300 transition-colors duration-300 hover:text-[#C5A059]"
          >
            <Phone className="h-3.5 w-3.5" />
            {CONTACT.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="nav-enquiry-button"
            className="bg-[#C5A059] px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#030F26] transition-colors duration-300 hover:bg-[#E2C08D]"
          >
            Enquire
          </Link>
        </div>
        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key={pathname}
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#1E3A6E]/50 bg-[#030F26]/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.testId}-mobile`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl font-medium tracking-wide transition-colors duration-300 ${
                        isActive ? "text-[#C5A059]" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                data-testid="nav-mobile-phone-link"
                href={`tel:${CONTACT.phoneRaw}`}
                className="mt-4 flex items-center gap-2 font-mono text-sm text-[#C5A059]"
              >
                <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
